import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Typography, Card, CardContent, Button, ButtonGroup, Modal, Box, TextField, Checkbox, FormControlLabel, CardActions, Tooltip, IconButton, Skeleton } from '@mui/material';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import LaunchIcon from '@mui/icons-material/Launch';
import DeleteIcon from '@mui/icons-material/Delete';
import GetAppIcon from '@mui/icons-material/GetApp';
import TableChartIcon from '@mui/icons-material/TableChart';
import EditIcon from '@mui/icons-material/Edit';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import { Users, FileText, Package, Plus, Phone } from 'lucide-react';
import { styled } from '@mui/material/styles';

import SimpleSnackbar from './SimpleSnackbar';
import { useDirectionContext } from '../contexts/DirectionContext';
import { useTranslation } from 'react-i18next';
import notifications from '../utils/notifications';
import { colors, spacing, typography, shadows, radius, transitions } from '../theme/tokens';

// Styled components using design tokens
const StyledButton = styled(Button)(({ theme, variant, actiontype }) => ({
    textTransform: 'none',
    borderRadius: radius.button,
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.semibold,
    padding: `${spacing.sm} ${spacing.lg}`,
    transition: transitions.all,
    boxShadow: shadows.none,

    ...(variant === 'contained' && {
        backgroundColor: colors.primary.main,
        color: colors.primary.contrast,
        border: 'none',
        '&:hover': actiontype === 'primary' ? {
            // Primary actions: Bold hover with scale
            backgroundColor: colors.primary.dark,
            transform: 'scale(1.02)',
            boxShadow: `0 6px 16px ${colors.primary.main}4d`, // 30% opacity
        } : {
            // Default contained hover
            backgroundColor: colors.primary.dark,
            boxShadow: `0 4px 12px ${colors.primary.main}40`, // 25% opacity
        },
        '&:disabled': {
            backgroundColor: colors.neutral[400],
            color: colors.neutral.white,
        }
    }),

    ...(variant === 'outlined' && {
        backgroundColor: colors.background.paper,
        color: colors.primary.main,
        border: `2px solid ${colors.primary.main}`,
        '&:hover': {
            // Secondary actions: Subtle border color change only
            borderColor: colors.primary.dark,
        },
        '&:disabled': {
            backgroundColor: colors.neutral[50],
            color: colors.neutral[400],
            borderColor: colors.neutral[400],
        }
    })
}));

const BrandCard = styled(Card)({
    borderRadius: radius.card,
    boxShadow: `0 2px 8px ${colors.primary.main}14`, // 8% opacity
    border: `2px solid ${colors.slate[200]}`,
    transition: transitions.all,
    backgroundColor: colors.background.paper,
    '&:hover': {
        boxShadow: `0 4px 16px ${colors.primary.main}1f`, // 12% opacity
        borderColor: colors.secondary.main,
    }
});

const TabButton = styled('button')(({ isActive }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: spacing.sm,
    padding: `14px ${spacing.lg}`,
    borderRadius: radius.button,
    fontWeight: typography.fontWeight.semibold,
    fontSize: typography.fontSize.base,
    transition: transitions.all,
    background: isActive ? colors.primary.main : colors.background.paper,
    color: isActive ? colors.primary.contrast : colors.slate[500],
    border: `2px solid ${isActive ? colors.primary.main : colors.slate[200]}`,
    cursor: 'pointer',
    minWidth: '140px',
    justifyContent: 'center',
    boxShadow: shadows.none,
    '&:hover': {
        backgroundColor: isActive ? colors.primary.dark : '#f0f4ff',
        borderColor: isActive ? colors.primary.dark : colors.secondary.main,
        color: isActive ? colors.primary.contrast : colors.primary.main
    }
}));

const StatusBadge = styled('div')(({ status }) => ({
    padding: '6px 14px',
    borderRadius: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.semibold,
    backgroundColor: status === 'active' ? colors.info.bg : colors.error.bg,
    color: status === 'active' ? colors.secondary.main : colors.error.main,
    border: `2px solid ${status === 'active' ? colors.secondary.main : colors.error.main}`,
    '&::before': {
        content: '""',
        width: '6px',
        height: '6px',
        borderRadius: radius.full,
        backgroundColor: status === 'active' ? colors.secondary.main : colors.error.main
    }
}));

const StyledInput = styled('input')({
    border: `2px solid ${colors.slate[200]}`,
    borderRadius: radius.input,
    padding: `${spacing.md} ${spacing.md}`,
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    color: colors.text.primary,
    backgroundColor: colors.background.paper,
    transition: transitions.all,
    outline: 'none',
    width: '100%',
    '&:focus': {
        borderColor: colors.secondary.main,
        boxShadow: `0 0 0 3px ${colors.secondary.main}1a`, // 10% opacity
    },
    '&::placeholder': {
        color: colors.text.disabled,
    }
});

const MemberCard = styled('div')({
    background: colors.background.paper,
    borderRadius: radius.card,
    padding: spacing.lg,
    marginBottom: spacing.md,
    border: `2px solid ${colors.slate[200]}`,
    transition: transitions.all,
    boxShadow: `0 1px 3px ${colors.primary.main}0d`, // 5% opacity
    '&:hover': {
        borderColor: colors.secondary.main,
        boxShadow: `0 4px 12px ${colors.secondary.main}26`, // 15% opacity
        transform: 'translateX(-4px)'
    }
});

const PageContainer = styled('div')({
    minHeight: '100vh',
    backgroundColor: colors.background.dark,
    padding: spacing.lg,
    fontFamily: typography.fontFamily.primary
});

const LoadingBar = styled('div')({
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    backgroundColor: colors.primary.main,
    zIndex: 9999
});

const ContentWrapper = styled('div')({
    maxWidth: '1200px',
    margin: '0 auto'
});

const HeaderSection = styled('div')({
    backgroundColor: colors.primary.main,
    borderRadius: radius.card,
    padding: '48px',
    marginBottom: spacing.xl,
    boxShadow: shadows.sm
});

const HeaderContent = styled('div')({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: spacing.xl
});

const HeaderTitle = styled(Typography)({
    fontSize: typography.fontSize['4xl'],
    fontWeight: typography.fontWeight.bold,
    color: colors.primary.contrast,
    marginBottom: spacing.sm,
    letterSpacing: '-0.5px'
});

const HeaderSubtitle = styled(Typography)({
    fontSize: typography.fontSize.lg,
    color: '#e0e7ff',
    fontWeight: typography.fontWeight.regular
});

const MemberCountCard = styled('div')({
    textAlign: 'center',
    padding: `${spacing.lg} 28px`,
    backgroundColor: colors.background.paper,
    borderRadius: radius.card,
    minWidth: '120px'
});

const MemberCountLabel = styled(Typography)({
    color: colors.slate[500],
    marginBottom: spacing.xs,
    fontWeight: typography.fontWeight.medium,
    fontSize: typography.fontSize.sm,
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
});

const MemberCountValue = styled(Typography)({
    fontWeight: typography.fontWeight.bold,
    color: colors.primary.main,
    fontSize: '40px',
    lineHeight: '1'
});

const TabsContainer = styled('div')({
    display: 'flex',
    gap: spacing.md,
    justifyContent: 'center',
    flexWrap: 'wrap',
    marginBottom: spacing.xl,
    padding: spacing.sm,
    backgroundColor: colors.background.paper,
    borderRadius: '16px',
    boxShadow: `0 2px 12px ${colors.primary.main}14`,
    border: `2px solid ${colors.slate[200]}`
});

const SectionTitle = styled(Typography)({
    fontWeight: '600',
    marginBottom: '32px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    color: '#333333',
    lineHeight: '1.4'
});

const AddMemberSection = styled('div')({
    backgroundColor: colors.neutral[50],
    borderRadius: '20px',
    padding: '32px',
    border: '2px dashed #EAEEF2'
});

const PhoneInputContainer = styled('div')({
    direction: 'ltr',
    display: 'flex',
    gap: '16px',
    alignItems: 'stretch',
    flexWrap: 'wrap'
});

const MemberIconContainer = styled('div')({
    width: '56px',
    height: '56px',
    backgroundColor: colors.primary.main,
    borderRadius: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: 'rgba(26, 0, 0, 0.1) 0 4px 12px'
});

const MemberInfo = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: '20px'
});

const MemberPhone = styled(Typography)({
    fontWeight: '600',
    direction: 'ltr',
    color: '#333333'
});

const MemberRole = styled(Typography)({
    color: '#666666',
    fontWeight: '500'
});

// Documents Tab styled components
const TemplatesGrid = styled('div')(({ $direction }) => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))',
    gap: spacing.lg,
    direction: $direction || 'rtl',
}));

const springEasing = 'cubic-bezier(0.34, 1.56, 0.64, 1)';

const TemplateCard = styled('div')({
    backgroundColor: colors.background.paper,
    borderRadius: radius.xl,
    border: `2px solid ${colors.slate[200]}`,
    overflow: 'hidden',
    transition: `transform 0.3s ${springEasing}, border-color 0.3s ease`,
    boxShadow: shadows.card,
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    '&::after': {
        content: '""',
        position: 'absolute',
        inset: 0,
        borderRadius: 'inherit',
        boxShadow: shadows.xl,
        opacity: 0,
        transition: `opacity 0.3s ease`,
        pointerEvents: 'none',
    },
    '&:hover': {
        transform: 'scale(1.03)',
        borderColor: colors.secondary.main,
        '&::after': {
            opacity: 1,
        },
    },
    '&:active': {
        transform: 'scale(0.97)',
    },
    '@media (prefers-reduced-motion: reduce)': {
        transition: 'none',
        '&::after': { transition: 'none' },
        '&:hover': { transform: 'none' },
        '&:active': { transform: 'none' },
    },
});

const StatusIndicatorLine = styled('div')({
    height: '4px',
    background: `linear-gradient(90deg, ${colors.primary.main} 0%, ${colors.secondary.main} 100%)`,
    width: '100%'
});

const TemplateCardContent = styled('div')({
    padding: spacing.lg,
    flex: 1,
    display: 'flex',
    flexDirection: 'column'
});

const TemplateHeaderRow = styled('div')({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '20px',
    gap: spacing.md
});

const TemplateNumberBadge = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    backgroundColor: colors.info.bg,
    padding: '8px 14px',
    borderRadius: radius.card,
    border: `1px solid ${colors.info.border}`,
});

const TemplateNumberCircle = styled('div')({
    width: '32px',
    height: '32px',
    backgroundColor: colors.primary.main,
    borderRadius: radius.button,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: colors.neutral.white,
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.black
});

const TemplateTitle = styled('h3')({
    fontSize: '20px',
    fontWeight: typography.fontWeight.semibold,
    color: colors.text.primary,
    marginBottom: spacing.md,
    lineHeight: '1.5',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minHeight: '56px'
});

const MetadataGrid = styled('div')({
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: spacing.md,
    marginBottom: '20px'
});

const MetadataCard = styled('div')(({ $bgColor, $borderColor }) => ({
    backgroundColor: $bgColor || colors.neutral[50],
    padding: spacing.md,
    borderRadius: radius.card,
    border: `1px solid ${$borderColor || colors.slate[200]}`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px'
}));

const SubmissionCount = styled('span')(({ $color }) => ({
    fontSize: '24px',
    fontWeight: typography.fontWeight.black,
    color: $color || colors.text.primary,
    lineHeight: '1'
}));

const MetadataLabel = styled('span')({
    fontSize: typography.fontSize.xs,
    color: colors.slate[500],
    fontWeight: typography.fontWeight.semibold
});

const QuickActionsGrid = styled('div')({
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: spacing.sm,
    marginTop: 'auto'
});

const PrimaryActionLink = styled('a')({
    textDecoration: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    padding: spacing.md,
    backgroundColor: colors.primary.main,
    color: colors.neutral.white,
    borderRadius: radius.lg,
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.semibold,
    transition: `transform 0.2s ${springEasing}, background-color 0.2s ease`,
    border: 'none',
    cursor: 'pointer',
    '&:hover': {
        backgroundColor: colors.primary.dark,
        transform: 'scale(1.02)',
    },
    '&:active': {
        transform: 'scale(0.97)',
    },
    '@media (prefers-reduced-motion: reduce)': {
        transition: 'none',
        '&:hover': { transform: 'none' },
        '&:active': { transform: 'none' },
    },
});

const SecondaryActionButton = styled('button')({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    padding: spacing.md,
    backgroundColor: colors.background.paper,
    color: colors.secondary.main,
    borderRadius: radius.lg,
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.semibold,
    transition: `transform 0.2s ${springEasing}, background-color 0.2s ease, color 0.2s ease`,
    border: `2px solid ${colors.secondary.main}`,
    cursor: 'pointer',
    '&:hover': {
        backgroundColor: colors.secondary.main,
        color: colors.neutral.white,
        transform: 'scale(1.02)',
    },
    '&:active': {
        transform: 'scale(0.97)',
    },
    '@media (prefers-reduced-motion: reduce)': {
        transition: 'none',
        '&:hover': { transform: 'none' },
        '&:active': { transform: 'none' },
    },
});

const SecondaryActionsGrid = styled('div')({
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: spacing.sm,
    marginTop: spacing.md,
    paddingTop: spacing.md,
    borderTop: `1px solid ${colors.slate[200]}`
});

const TertiaryActionButton = styled('button')(({ $variant }) => {
    const variants = {
        copy: {
            backgroundColor: colors.info.bg,
            color: colors.info.dark,
            border: `1px solid ${colors.info.border}`,
            hoverBg: colors.info.bg,
        },
        download: {
            backgroundColor: colors.slate[50],
            color: colors.text.secondary,
            border: `1px solid ${colors.slate[200]}`,
            hoverBg: colors.slate[200],
        },
        edit: {
            backgroundColor: colors.warning.bg,
            color: colors.warning.main,
            border: `1px solid ${colors.warning.border}`,
            hoverBg: colors.warning.bg,
        },
        delete: {
            backgroundColor: colors.error.bg,
            color: colors.error.main,
            border: `1px solid ${colors.error.border}`,
            hoverBg: colors.error.dark,
        },
    };

    const variantStyles = variants[$variant] || variants.copy;

    return {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
        padding: '10px',
        backgroundColor: variantStyles.backgroundColor,
        color: variantStyles.color,
        borderRadius: radius.button,
        fontSize: typography.fontSize.xs,
        fontWeight: typography.fontWeight.semibold,
        border: variantStyles.border,
        cursor: 'pointer',
        transition: `transform 0.2s ${springEasing}, background-color 0.2s ease`,
        '&:hover:not(:disabled)': {
            backgroundColor: variantStyles.hoverBg,
            transform: 'scale(1.05)',
            ...(($variant === 'delete' || $variant === 'secondary') && {
                color: colors.neutral.white,
            }),
        },
        '&:active:not(:disabled)': {
            transform: 'scale(0.95)',
        },
        '&:disabled': {
            cursor: 'not-allowed',
            opacity: 0.6,
        },
        '@media (prefers-reduced-motion: reduce)': {
            transition: 'none',
            '&:hover:not(:disabled)': { transform: 'none' },
            '&:active:not(:disabled)': { transform: 'none' },
        },
    };
});

const TertiaryActionLink = styled('a')(({ $variant }) => {
    const variants = {
        edit: {
            backgroundColor: colors.warning.bg,
            color: colors.warning.main,
            border: `1px solid ${colors.warning.border}`,
            hoverBg: colors.warning.bg,
        },
    };

    const variantStyles = variants[$variant] || variants.edit;

    return {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
        padding: '10px',
        backgroundColor: variantStyles.backgroundColor,
        color: variantStyles.color,
        borderRadius: radius.button,
        fontSize: typography.fontSize.xs,
        fontWeight: typography.fontWeight.semibold,
        border: variantStyles.border,
        cursor: 'pointer',
        textDecoration: 'none',
        transition: `transform 0.2s ${springEasing}, background-color 0.2s ease`,
        '&:hover': {
            backgroundColor: variantStyles.hoverBg,
            transform: 'scale(1.05)',
        },
        '&:active': {
            transform: 'scale(0.95)',
        },
        '@media (prefers-reduced-motion: reduce)': {
            transition: 'none',
            '&:hover': { transform: 'none' },
            '&:active': { transform: 'none' },
        },
    };
});

const RenameIconButton = styled('button')({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    borderRadius: radius.button,
    border: 'none',
    backgroundColor: 'transparent',
    color: colors.text.muted,
    cursor: 'pointer',
    transition: `color 0.2s ease, background-color 0.2s ease`,
    '&:hover': {
        color: colors.primary.main,
        backgroundColor: colors.state.hover,
    },
});

export default function Org(props) {
    const [org, setOrg] = useState(false);
    const [loading, setLoading] = useState(false);
    const [newMember, setNewMember] = useState("");
    const [count, setCount] = useState(0);
    const [openBundleCreator, setOpenBundleCreator] = useState(false);
    const [bundleName, setBundleName] = useState([""]);
    const [selectedTemplates, setSelectedTemplates] = useState([]);
    const [selectedDiv, setSelectedDiv] = useState(2);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [deleting, setDeleting] = useState(false);
    const [showInactive, setShowInactive] = useState(false);
    const [removeConfirm, setRemoveConfirm] = useState(null); // member phone being confirmed for removal
    const [renameModalOpen, setRenameModalOpen] = useState(false);
    const [renameTarget, setRenameTarget] = useState(null); // { id, currentName }
    const [newTemplateName, setNewTemplateName] = useState('');
    const [renaming, setRenaming] = useState(false);
    const direction = useDirectionContext();
    const { t } = useTranslation();

    // Focus management refs (AIF-20)
    const bundleNameInputRef = useRef(null);
    const createBundleButtonRef = useRef(null);
    const renameButtonRef = useRef(null);
    const renameInputRef = useRef(null);

    const navigate = useNavigate();
    const { key } = useParams();

    const location = useLocation();
    const currentPath = location.pathname;
    const currentProtocol = window.location.protocol;
    const currentDomain = window.location.hostname;
    const currentPort = window.location.port;

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                console.log(props.user);
                const access = await axios.get(`${window.AppConfig.serverDomain}/api/organzations/get-org-ids/${props.user}`);
                console.log(access.data);
                if (access.data.result.includes(key) || access.manager) {
                    const response = await axios.get(`${window.AppConfig.serverDomain}/api/organzations/get-org/${key}`);
                    console.log(response.data);
                    setOrg(response.data);
                    setSelectedTemplates(response.data.templates.map((item, index) => ({
                        ...item,
                        index: index,
                        selected: false,
                    })))
                } else {
                    notifications.error(t('org.errors.noAccess'));
                }
                setLoading(false);
            } catch (error) {
                console.error('Error fetching data:', error);
                setLoading(false);
            }
        };

        fetchData();
    }, [count]);

    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Auto-focus bundle name input when modal opens (AIF-20)
    useEffect(() => {
        if (openBundleCreator && bundleNameInputRef.current) {
            // Small delay to ensure modal is fully rendered
            setTimeout(() => {
                bundleNameInputRef.current?.focus();
            }, 100);
        }
    }, [openBundleCreator]);

    // Focus trap for modal (AIF-26)
    useEffect(() => {
        if (!openBundleCreator) return;

        const handleTabKey = (e) => {
            if (e.key !== 'Tab') return;

            // Get all focusable elements within the modal
            const modal = document.querySelector('[role="dialog"]');
            if (!modal) return;

            const focusableElements = modal.querySelectorAll(
                'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            );
            const firstElement = focusableElements[0];
            const lastElement = focusableElements[focusableElements.length - 1];

            // If shift+tab on first element, focus last element
            if (e.shiftKey && document.activeElement === firstElement) {
                e.preventDefault();
                lastElement.focus();
            }
            // If tab on last element, focus first element
            else if (!e.shiftKey && document.activeElement === lastElement) {
                e.preventDefault();
                firstElement.focus();
            }
        };

        document.addEventListener('keydown', handleTabKey);
        return () => document.removeEventListener('keydown', handleTabKey);
    }, [openBundleCreator]);

    function handleCreateTemplate(event, orgID) {
        navigate(`/create-template/${orgID}`);
    }

    function convertToInternationalFormat(phoneNumber) {
        const cleanedNumber = phoneNumber.replace(/\D/g, '');
        if (cleanedNumber.startsWith('0')) {
            return '+972' + cleanedNumber.slice(1);
        }
        return cleanedNumber;
    }

    async function handleAddMember(event, orgID) {
        if (newMember.length < 10) {
            notifications.error(t('org.errors.phoneNumberIncomplete'));
            return;
        }
        const formatted = convertToInternationalFormat(newMember);
        const localUrl = `${window.AppConfig.serverDomain}/api/organzations/add-member/`;
        const formData = {
            orgID: orgID,
            newMember: formatted,
        };
        try {
            setLoading(true);
            const response1 = await axios.post(localUrl, formData);
            console.log(response1.data.orgID);
        } catch (error) {
            console.error('Error:', error);
            setLoading(false);
        }
        setNewMember("");
        setCount(count + 1);
    }

    async function handleRemoveMember(phone) {
        try {
            setLoading(true);
            await axios.post(`${window.AppConfig.serverDomain}/api/organzations/remove-member/`, {
                orgID: org.orgID,
                phone: phone,
            });
            setOrg(prev => ({
                ...prev,
                members: prev.members.filter(m => m !== phone),
                removedMembers: [
                    ...(prev.removedMembers || []),
                    { phone: '0' + phone.slice(4), removedAt: new Date().toISOString() }
                ],
            }));
        } catch (error) {
            console.error('Error removing member:', error);
            notifications.error('שגיאה בהסרת החבר');
        } finally {
            setLoading(false);
            setRemoveConfirm(null);
        }
    }

    function showSubmittedData(event, templateID) {
        const fetchSubmittedData = async () => {
            try {
                navigate(`/submitted/${templateID}`);
            } catch (error) {
                console.error('Error fetching data:', error);
                setLoading(false);
            }
        };
        fetchSubmittedData();
    }

    async function downloadSubmittedData(event, templateID, templateName) {
        const fetchSubmittedData = async () => {
            try {
                setLoading(true);
                const response = await axios.get(`${window.AppConfig.serverDomain}/api/organzations/submitted/${templateID}`);
                console.log(JSON.stringify(response.data));
                const buffer = await generateExcelFile(JSON.stringify(response.data));
                const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                saveAs(blob, `${templateName.split(".")[0]}.xlsx`);
                setLoading(false);
            } catch (error) {
                console.error('Error fetching data:', error);
                setLoading(false);
            }
        };
        await fetchSubmittedData();
    }

    const generateExcelFile = async (submittedData) => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Sheet1');

        const inputsHeaders = [
            ...Object.keys(JSON.parse(submittedData)[0].staticFields).map(key => ({ header: key, key: key, width: key.length * 1.2 })),
            ...JSON.parse(submittedData).pop().extraFields.map(item => ({ header: item.fieldID, key: item.fieldID, width: item.fieldID.length })),
        ];

        worksheet.columns =
            [
                { header: 'pdfFileURL', key: 'pdfFileURL', width: 60 },
                ...inputsHeaders,
            ];

        JSON.parse(submittedData).forEach((submittedDocument, index) => {
            if (submittedDocument.accepted) {
                const extraFields = submittedDocument.extraFields.reduce((acc, item) => {
                    acc[item.fieldID] = item.fieldValue;
                    return acc;
                }, {});

                const inputValues = { ...submittedDocument.staticFields, ...extraFields }
                const newRow = { ...{ pdfFileURL: submittedDocument.signedPdf }, ...inputValues };
                worksheet.addRow(newRow);
            }
        });

        const buffer = await workbook.xlsx.writeBuffer();
        return buffer;
    };

    // Modal focus management (AIF-20)
    const handleOpen = () => {
        // Store reference to the button that opened the modal
        createBundleButtonRef.current = document.activeElement;
        setOpenBundleCreator(true);
    };

    const handleClose = () => {
        setOpenBundleCreator(false);
        // Return focus to the button that opened the modal
        setTimeout(() => {
            createBundleButtonRef.current?.focus();
        }, 100);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        const localUrl = `${window.AppConfig.serverDomain}/api/organzations/create-bundle/`;
        const extractedSelectedTemplates = selectedTemplates.filter((option) => option.selected).map(({ index, selected, ...rest }) => rest);
        const formData = {
            bundleName: bundleName,
            bundleTemplates: extractedSelectedTemplates,
            orgID: org.orgID
        };
        try {
            const response = await axios.post(localUrl, formData);
            console.log(response.data);
            setCount(count + 1);
        } catch (error) {
            console.error('Error:', error);
        }
        handleClose();
    };

    const handleInputChange = (event) => {
        setBundleName(event.target.value);
    };

    const handleOptionChange = (optionId) => {
        setSelectedTemplates((prevOptions) =>
            prevOptions.map((option) =>
                option.index === optionId ? { ...option, selected: !option.selected } : option
            )
        );
    };

    const port = window.AppConfig.serverDomain.includes('localhost') ? ":" + currentPort : "";

    const handleDivChange = (divNumber) => {
        setSelectedDiv(divNumber);
    };

    // Keyboard navigation for tabs (AIF-20)
    const handleTabKeyDown = (event, currentTab) => {
        let newTab = currentTab;

        // Arrow key navigation
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
            event.preventDefault();

            if (event.key === 'ArrowRight') {
                // Move to previous tab (RTL)
                newTab = currentTab === 1 ? 3 : currentTab - 1;
            } else if (event.key === 'ArrowLeft') {
                // Move to next tab (RTL)
                newTab = currentTab === 3 ? 1 : currentTab + 1;
            }

            handleDivChange(newTab);
        }

        // Enter or Space to activate tab
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleDivChange(currentTab);
        }
    };

    const calculateFontSize = () => {
        return Math.max(windowWidth * 0.015, 12);
    };

    function removeAfterLastUnderscore(str) {
        const lastUnderscoreIndex = str.lastIndexOf('_');
        if (lastUnderscoreIndex !== -1) {
            return str.substring(0, lastUnderscoreIndex);
        }
        return str;
    }

    const handleDelete = async (templateID) => {
        const userConfirmed = await notifications.confirm(t('org.errors.deleteConfirm'));
        if (!userConfirmed) return;
        setDeleting(true);
        const endpointURL = `${window.AppConfig.serverDomain}/api/organzations/disable-template`;
        try {
            const response = await axios.post(endpointURL, { templateID });
            if (response.status === 200) {
                setOrg(prev => ({
                    ...prev,
                    templates: prev.templates.map(t =>
                        t.id === templateID ? { ...t, active: false } : t
                    )
                }));
            } else {
                console.error("Error deleting the template:", response.data);
            }
        } catch (error) {
            console.error("Error:", error);
        } finally {
            setDeleting(false);
        }
    };

    const openRenameModal = (template) => {
        renameButtonRef.current = document.activeElement;
        const displayName = removeAfterLastUnderscore(template.name.split('.')[0]);
        setRenameTarget({ id: template.id, currentName: displayName });
        setNewTemplateName(displayName);
        setRenameModalOpen(true);
        setTimeout(() => {
            renameInputRef.current?.focus();
            renameInputRef.current?.select();
        }, 100);
    };

    const closeRenameModal = () => {
        setRenameModalOpen(false);
        setRenameTarget(null);
        setNewTemplateName('');
        setTimeout(() => {
            renameButtonRef.current?.focus();
        }, 100);
    };

    const handleRename = async () => {
        if (!renameTarget || !newTemplateName.trim() || newTemplateName.trim() === renameTarget.currentName) return;
        setRenaming(true);
        try {
            await axios.patch(`${window.AppConfig.serverDomain}/api/organzations/rename-template`, {
                templateID: renameTarget.id,
                name: newTemplateName.trim(),
            });
            setOrg(prev => ({
                ...prev,
                templates: prev.templates.map(t =>
                    t.id === renameTarget.id ? { ...t, name: newTemplateName.trim() } : t
                ),
            }));
            notifications.success(t('org.documents.renameSuccess'));
            closeRenameModal();
        } catch (error) {
            console.error('Error renaming template:', error);
            notifications.error(t('org.documents.renameError'));
        } finally {
            setRenaming(false);
        }
    };

    return (
        <PageContainer>
            {/* Loading Bar */}
            {loading && <LoadingBar />}

            {loading ? (
                /* Skeleton Loading State */
                <ContentWrapper>
                    {/* Header Skeleton */}
                    <HeaderSection>
                        <HeaderContent>
                            <div>
                                <Skeleton variant="text" width="60%" height={48} sx={{ bgcolor: 'rgba(255, 255, 255, 0.3)' }} />
                                <Skeleton variant="text" width="40%" height={28} sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', marginTop: spacing.sm }} />
                            </div>
                            <MemberCountCard>
                                <Skeleton variant="text" width={80} height={20} sx={{ marginBottom: spacing.xs }} />
                                <Skeleton variant="text" width={40} height={40} />
                            </MemberCountCard>
                        </HeaderContent>
                    </HeaderSection>

                    {/* Tabs Skeleton */}
                    <TabsContainer>
                        <Skeleton variant="rounded" width={140} height={50} sx={{ borderRadius: radius.button }} />
                        <Skeleton variant="rounded" width={140} height={50} sx={{ borderRadius: radius.button }} />
                        <Skeleton variant="rounded" width={140} height={50} sx={{ borderRadius: radius.button }} />
                    </TabsContainer>

                    {/* Content Skeleton */}
                    <BrandCard>
                        <CardContent sx={{ padding: '32px' }}>
                            {/* Templates Grid Skeleton */}
                            <TemplatesGrid $direction={direction}>
                                {[1, 2, 3].map((index) => (
                                    <TemplateCard key={index} sx={{ pointerEvents: 'none' }}>
                                        <Skeleton variant="rectangular" width="100%" height={4} />
                                        <Box sx={{ padding: spacing.lg }}>
                                            <Skeleton variant="rounded" width="100%" height={60} sx={{ marginBottom: spacing.md }} />
                                            <Skeleton variant="text" width="80%" height={32} sx={{ marginBottom: spacing.md }} />
                                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.md, marginBottom: spacing.md }}>
                                                <Skeleton variant="rounded" height={80} />
                                                <Skeleton variant="rounded" height={80} />
                                            </Box>
                                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: spacing.sm }}>
                                                <Skeleton variant="rounded" height={44} />
                                                <Skeleton variant="rounded" height={44} />
                                            </Box>
                                        </Box>
                                    </TemplateCard>
                                ))}
                            </TemplatesGrid>
                        </CardContent>
                    </BrandCard>
                </ContentWrapper>
            ) : org && (
                <ContentWrapper>
                    {/* Header Section */}
                    <HeaderSection>
                        <HeaderContent>
                            <div>
                                <HeaderTitle>
                                    {org.orgName}
                                </HeaderTitle>
                                <HeaderSubtitle>
                                    {t('org.organizationManagement')}
                                </HeaderSubtitle>
                            </div>
                            <MemberCountCard>
                                <MemberCountLabel>
                                    {t('org.totalMembers')}
                                </MemberCountLabel>
                                <MemberCountValue>
                                    {org.members?.length || 0}
                                </MemberCountValue>
                            </MemberCountCard>
                        </HeaderContent>
                    </HeaderSection>

                    {/* Navigation Tabs */}
                    <TabsContainer
                        role="tablist"
                        aria-label={t('org.tabs.navigationAriaLabel')}
                    >
                        <TabButton
                            onClick={() => handleDivChange(1)}
                            onKeyDown={(e) => handleTabKeyDown(e, 1)}
                            isActive={selectedDiv === 1}
                            role="tab"
                            aria-selected={selectedDiv === 1}
                            aria-controls="users-panel"
                            aria-label={t('org.tabs.usersAriaLabel')}
                            tabIndex={selectedDiv === 1 ? 0 : -1}
                        >
                            <Users size={20} aria-hidden="true" />
                            {t('org.tabs.users')}
                        </TabButton>
                        <TabButton
                            onClick={() => handleDivChange(2)}
                            onKeyDown={(e) => handleTabKeyDown(e, 2)}
                            isActive={selectedDiv === 2}
                            role="tab"
                            aria-selected={selectedDiv === 2}
                            aria-controls="documents-panel"
                            aria-label={t('org.tabs.documentsAriaLabel')}
                            tabIndex={selectedDiv === 2 ? 0 : -1}
                        >
                            <FileText size={20} aria-hidden="true" />
                            {t('org.tabs.documents')}
                        </TabButton>
                        <TabButton
                            onClick={() => handleDivChange(3)}
                            onKeyDown={(e) => handleTabKeyDown(e, 3)}
                            isActive={selectedDiv === 3}
                            role="tab"
                            aria-selected={selectedDiv === 3}
                            aria-controls="bundles-panel"
                            aria-label={t('org.tabs.bundlesAriaLabel')}
                            tabIndex={selectedDiv === 3 ? 0 : -1}
                        >
                            <Package size={20} aria-hidden="true" />
                            {t('org.tabs.bundles')}
                        </TabButton>
                    </TabsContainer>

                    {/* Content Sections */}
                    <Box>
                        {/* Users Tab */}
                        {selectedDiv === 1 && (
                            <BrandCard
                                role="tabpanel"
                                id="users-panel"
                                aria-labelledby="users-tab"
                            >
                                <CardContent style={{ padding: '32px' }}>
                                    <Typography variant="h5" style={{
                                        fontWeight: '600',
                                        marginBottom: '32px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '12px',
                                        color: '#333333',
                                        lineHeight: '1.4'
                                    }}>
                                        <Users size={28} />
                                        {t('org.users.title')}
                                    </Typography>

                                    {/* Members List */}
                                    <div style={{ marginBottom: '32px' }}>
                                        {org.members.map((member, index) => (
                                            <MemberCard key={index}>
                                                <MemberInfo style={{ justifyContent: 'space-between' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                                                        <MemberIconContainer>
                                                            <Phone size={24} style={{ color: colors.neutral.white }} />
                                                        </MemberIconContainer>
                                                        <div>
                                                            <MemberPhone variant="h6">
                                                                {'0' + member.slice(4)}
                                                            </MemberPhone>
                                                            <MemberRole variant="body2">
                                                                {t('org.users.memberRole')}
                                                            </MemberRole>
                                                        </div>
                                                    </div>
                                                    {removeConfirm === member ? (
                                                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                                            <span style={{ fontSize: '13px', color: '#666' }}>להסיר חבר זה?</span>
                                                            <button
                                                                onClick={() => handleRemoveMember(member)}
                                                                style={{ padding: '6px 14px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}
                                                            >
                                                                אישור
                                                            </button>
                                                            <button
                                                                onClick={() => setRemoveConfirm(null)}
                                                                style={{ padding: '6px 12px', background: 'transparent', color: '#666', border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}
                                                            >
                                                                ביטול
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <button
                                                            onClick={() => setRemoveConfirm(member)}
                                                            style={{ padding: '6px 14px', background: 'transparent', color: '#ef4444', border: '1px solid #fecaca', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 500 }}
                                                        >
                                                            הסר
                                                        </button>
                                                    )}
                                                </MemberInfo>
                                            </MemberCard>
                                        ))}

                                        {/* Removed Members (read-only) */}
                                        {org.removedMembers && org.removedMembers.length > 0 && (
                                            <div style={{ marginTop: '24px', paddingTop: '20px', borderTop: `1px solid ${colors.slate[200]}` }}>
                                                <Typography variant="body2" style={{ fontSize: '12px', fontWeight: 600, color: '#aaa', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                    חברים שהוסרו
                                                </Typography>
                                                {org.removedMembers.map((rm, idx) => (
                                                    <div key={idx} style={{
                                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                                        padding: '10px 16px', background: colors.neutral[50], borderRadius: '8px', marginBottom: '8px'
                                                    }}>
                                                        <span style={{ fontSize: '14px', color: '#999', direction: 'ltr' }}>{rm.phone}</span>
                                                        <span style={{ fontSize: '12px', color: '#bbb' }}>
                                                            {new Date(rm.removedAt).toLocaleDateString('he-IL')}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {/* Add New Member Section */}
                                    <AddMemberSection>
                                        <SectionTitle variant="h6">
                                            {t('org.users.addMemberTitle')}
                                        </SectionTitle>
                                        <PhoneInputContainer>
                                            <StyledInput
                                                type="text"
                                                value={newMember}
                                                placeholder={t('org.users.phonePlaceholder')}
                                                onChange={(event) => setNewMember(event.target.value)}
                                                style={{ flex: '1', minWidth: '250px' }}
                                                aria-label={t('org.users.phoneAriaLabel')}
                                                aria-required="true"
                                            />
                                            <StyledButton
                                                variant="contained"
                                                actiontype="primary"
                                                startIcon={<Plus size={20} aria-hidden="true" />}
                                                onClick={(event) => handleAddMember(event, `${org.orgID}`)}
                                                style={{ whiteSpace: 'nowrap' }}
                                                aria-label={t('org.users.addButtonAriaLabel')}
                                            >
                                                {t('org.users.addButton')}
                                            </StyledButton>
                                        </PhoneInputContainer>
                                    </AddMemberSection>
                                </CardContent>
                            </BrandCard>
                        )}

                        {/* Documents Tab */}
                        {selectedDiv === 2 && (
                            <BrandCard
                                role="tabpanel"
                                id="documents-panel"
                                aria-labelledby="documents-tab"
                            >
                                <CardContent style={{ padding: '32px' }}>
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        marginBottom: '32px',
                                        flexWrap: 'wrap',
                                        gap: '16px'
                                    }}>
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '12px'
                                        }}>
                                            <div
                                                style={{
                                                    width: '48px',
                                                    height: '48px',
                                                    backgroundColor: '#e3f2fd',
                                                    borderRadius: '12px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}
                                                aria-hidden="true"
                                            >
                                                <FileText size={24} color={colors.primary.main} />
                                            </div>
                                            <Typography style={{
                                                fontSize: '28px',
                                                fontWeight: '600',
                                                color: colors.text.primary,
                                                lineHeight: '1.4'
                                            }}>
                                                {t('org.documents.title')}
                                            </Typography>
                                        </div>
                                        <StyledButton
                                            variant="contained"
                                            actiontype="primary"
                                            startIcon={<Plus size={20} aria-hidden="true" />}
                                            onClick={(event) => handleCreateTemplate(event, `${org.orgID}`)}
                                            style={{
                                                padding: '12px 28px',
                                                fontSize: '16px'
                                            }}
                                            aria-label={t('org.documents.createButtonAriaLabel')}
                                        >
                                            {t('org.documents.createButton')}
                                        </StyledButton>
                                    </div>

                                    {/* Special Card for specific org */}
                                    {org.orgID === "jo62wr0f" && (
                                        <BrandCard style={{
                                            backgroundColor: '#f0fdf4',
                                            border: '2px solid #10b981',
                                            marginBottom: '24px'
                                        }}>
                                            <CardContent style={{ padding: '24px' }}>
                                                <Typography variant="h6" style={{
                                                    fontWeight: '700',
                                                    marginBottom: '16px',
                                                    color: '#333333'
                                                }}>
                                                    0. הסכם חינוך גיל רך
                                                </Typography>
                                                <SimpleSnackbar
                                                    templateLink={"https://sdenhemya.myvarno.io/"}
                                                    name={"https://sdenhemya.myvarno.io/"}
                                                />
                                            </CardContent>
                                            <CardActions style={{
                                                borderTop: '1px solid #10b981',
                                                padding: '20px',
                                                gap: '12px'
                                            }}>
                                                <StyledButton size="small" variant="outlined">
                                                    <a style={{
                                                        textDecoration: 'none',
                                                        color: 'inherit',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '8px'
                                                    }}
                                                        target="_blank"
                                                        href={"https://sdenhemya.myvarno.io/submit-table"}>
                                                        {t('org.documents.viewSubmissionsButton')} <LaunchIcon fontSize="small" />
                                                    </a>
                                                </StyledButton>
                                                <StyledButton variant="contained">
                                                    <a target="_blank"
                                                        href={`https://sdenhemya.myvarno.io/`}
                                                        style={{
                                                            textDecoration: 'none',
                                                            color: 'inherit',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '8px'
                                                        }}>
                                                        {t('org.documents.openInNewTab')} <LaunchIcon fontSize="small" />
                                                    </a>
                                                </StyledButton>
                                            </CardActions>
                                        </BrandCard>
                                    )}

                                    {/* Templates Filter Toggle */}
                                    {org.templates.some(t => t.active === false) && (
                                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
                                            <button
                                                onClick={() => setShowInactive(v => !v)}
                                                style={{
                                                    background: showInactive ? '#f1f5f9' : 'transparent',
                                                    border: '1px solid #e2e8f0',
                                                    borderRadius: '8px',
                                                    padding: '6px 14px',
                                                    fontSize: '13px',
                                                    color: '#64748b',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '6px'
                                                }}
                                            >
                                                {showInactive ? '👁 הסתר מושבתים' : '👁 הצג מושבתים'}
                                            </button>
                                        </div>
                                    )}

                                    {/* Templates Grid - Modern Card Design */}
                                    <TemplatesGrid $direction={direction}>
                                        {org.templates
                                            .filter(template => showInactive || template.active !== false)
                                            .map((template, index) => {
                                            const creationDate = template.createdAt ? new Date(template.createdAt) : new Date();
                                            const formattedDate = creationDate.toLocaleDateString('he-IL', {
                                                year: 'numeric',
                                                month: 'short',
                                                day: 'numeric'
                                            });

                                            // Get color based on submission count
                                            const getSubmissionColor = (count) => {
                                                if (count === 0) return {
                                                    bg: colors.slate[50],
                                                    border: colors.slate[200],
                                                    color: colors.text.disabled,
                                                };
                                                if (count >= 10) return {
                                                    bg: colors.success.bg,
                                                    border: colors.success.border,
                                                    color: colors.success.main,
                                                };
                                                return {
                                                    bg: colors.info.bg,
                                                    border: colors.info.border,
                                                    color: colors.info.dark,
                                                };
                                            };

                                            const submissionColors = getSubmissionColor(template.submitCount || 0);

                                            const isInactive = template.active === false;

                                            return (
                                                <TemplateCard key={index} style={{
                                                    opacity: isInactive ? 0.45 : 1,
                                                    filter: isInactive ? 'grayscale(0.6)' : 'none',
                                                    pointerEvents: isInactive ? 'none' : 'auto',
                                                }}>
                                                    {isInactive && (
                                                        <div style={{
                                                            position: 'absolute',
                                                            top: '10px',
                                                            insetInlineStart: '10px',
                                                            background: colors.slate[400],
                                                            color: colors.neutral.white,
                                                            fontSize: typography.fontSize.xs,
                                                            fontWeight: typography.fontWeight.semibold,
                                                            padding: '2px 8px',
                                                            borderRadius: radius.sm,
                                                            zIndex: 1,
                                                        }}>מושבת</div>
                                                    )}
                                                    <StatusIndicatorLine />

                                                    <TemplateCardContent>
                                                        <TemplateHeaderRow>
                                                            <TemplateNumberBadge>
                                                                <TemplateNumberCircle>
                                                                    {org.templates.length - index}
                                                                </TemplateNumberCircle>
                                                                <FileText size={20} color={colors.primary.main} strokeWidth={2.5} />
                                                            </TemplateNumberBadge>
                                                            <RenameIconButton
                                                                onClick={() => openRenameModal(template)}
                                                                aria-label={t('org.documents.renameAriaLabel', { name: removeAfterLastUnderscore(template.name.split('.')[0]) })}
                                                            >
                                                                <DriveFileRenameOutlineIcon style={{ fontSize: '18px' }} />
                                                            </RenameIconButton>
                                                        </TemplateHeaderRow>

                                                        <TemplateTitle>
                                                            {removeAfterLastUnderscore(template.name.split('.')[0])}
                                                        </TemplateTitle>

                                                        <MetadataGrid>
                                                            <MetadataCard $bgColor={submissionColors.bg} $borderColor={submissionColors.border}>
                                                                <SubmissionCount $color={submissionColors.color}>
                                                                    {template.submitCount || 0}
                                                                </SubmissionCount>
                                                                <MetadataLabel>
                                                                    {t('org.documents.submissions')}
                                                                </MetadataLabel>
                                                            </MetadataCard>

                                                            <MetadataCard $bgColor={colors.slate[50]} $borderColor={colors.slate[200]}>
                                                                <span style={{
                                                                    fontSize: typography.fontSize.sm,
                                                                    fontWeight: typography.fontWeight.bold,
                                                                    color: colors.slate[500],
                                                                    lineHeight: '1.2',
                                                                    textAlign: 'center',
                                                                }}>
                                                                    {formattedDate}
                                                                </span>
                                                                <MetadataLabel>
                                                                    {t('org.documents.creationDate')}
                                                                </MetadataLabel>
                                                            </MetadataCard>
                                                        </MetadataGrid>

                                                        {/* Quick Actions */}
                                                        <QuickActionsGrid>
                                                            <PrimaryActionLink
                                                                target="_blank"
                                                                href={`${currentProtocol}//${currentDomain}${port}/template/${template.id}`}
                                                                aria-label={t('org.documents.openDocumentAriaLabel', { name: removeAfterLastUnderscore(template.name.split('.')[0]) })}
                                                            >
                                                                <LaunchIcon style={{ fontSize: '18px' }} aria-hidden="true" />
                                                                {t('org.documents.openDocument')}
                                                            </PrimaryActionLink>

                                                            <SecondaryActionButton
                                                                onClick={(event) => showSubmittedData(event, template.id)}
                                                                aria-label={t('org.documents.viewSubmissionsAriaLabel', { name: removeAfterLastUnderscore(template.name.split('.')[0]) })}
                                                            >
                                                                <TableChartIcon style={{ fontSize: '18px' }} aria-hidden="true" />
                                                                {t('org.documents.viewSubmissions')}
                                                            </SecondaryActionButton>
                                                        </QuickActionsGrid>

                                                        {/* Secondary Actions */}
                                                        <SecondaryActionsGrid>
                                                            <TertiaryActionButton
                                                                $variant="copy"
                                                                onClick={() => {
                                                                    const link = `${currentProtocol}//${currentDomain}${port}/template/${template.id}`;
                                                                    navigator.clipboard.writeText(link);
                                                                    notifications.success(t('org.errors.linkCopied'));
                                                                }}
                                                                aria-label={t('org.documents.copyLinkAriaLabel', { name: removeAfterLastUnderscore(template.name.split('.')[0]) })}
                                                            >
                                                                <LaunchIcon style={{ fontSize: '16px' }} aria-hidden="true" />
                                                                {t('org.documents.shareLink')}
                                                            </TertiaryActionButton>

                                                            <TertiaryActionButton
                                                                $variant="download"
                                                                onClick={(event) => downloadSubmittedData(event, template.id, template.name)}
                                                                aria-label={t('org.documents.downloadAriaLabel', { name: removeAfterLastUnderscore(template.name.split('.')[0]) })}
                                                            >
                                                                <GetAppIcon style={{ fontSize: '16px' }} aria-hidden="true" />
                                                                {t('org.documents.download')}
                                                            </TertiaryActionButton>

                                                            <TertiaryActionLink
                                                                $variant="edit"
                                                                href={`${currentProtocol}//${currentDomain}${port}/update-doc/${removeAfterLastUnderscore(template.name.split('.')[0])}_${template.id}`}
                                                                target="_blank"
                                                                aria-label={t('org.documents.editAriaLabel', { name: removeAfterLastUnderscore(template.name.split('.')[0]) })}
                                                            >
                                                                <EditIcon style={{ fontSize: '16px' }} aria-hidden="true" />
                                                                {t('org.documents.update')}
                                                            </TertiaryActionLink>

                                                            <TertiaryActionButton
                                                                $variant="delete"
                                                                onClick={() => handleDelete(template.id)}
                                                                disabled={deleting}
                                                                aria-label={t('org.documents.deleteAriaLabel', { name: removeAfterLastUnderscore(template.name.split('.')[0]) })}
                                                                aria-disabled={deleting}
                                                            >
                                                                <DeleteIcon style={{ fontSize: '18px' }} aria-hidden="true" />
                                                            </TertiaryActionButton>
                                                        </SecondaryActionsGrid>
                                                    </TemplateCardContent>
                                                </TemplateCard>
                                            );
                                        })}
                                    </TemplatesGrid>
                                </CardContent>
                            </BrandCard>
                        )}

                        {/* Bundles Tab */}
                        {selectedDiv === 3 && (
                            <BrandCard
                                role="tabpanel"
                                id="bundles-panel"
                                aria-labelledby="bundles-tab"
                            >
                                <CardContent style={{ padding: '32px' }}>
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        marginBottom: '32px',
                                        flexWrap: 'wrap',
                                        gap: '16px'
                                    }}>
                                        <Typography variant="h5" style={{
                                            fontWeight: '600',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '12px',
                                            color: '#333333',
                                            lineHeight: '1.4'
                                        }}>
                                            <Package size={28} aria-hidden="true" />
                                            {t('org.bundles.title')}
                                        </Typography>
                                        <StyledButton
                                            variant="contained"
                                            actiontype="primary"
                                            startIcon={<Plus size={20} aria-hidden="true" />}
                                            onClick={handleOpen}
                                            style={{ backgroundColor: colors.secondary.main }}
                                            aria-label={t('org.bundles.createButtonAriaLabel')}
                                        >
                                            {t('org.bundles.createButton')}
                                        </StyledButton>
                                    </div>

                                    {org.bundles?.map((bundle, index) => (
                                        <BrandCard key={index} style={{ marginBottom: '24px' }}>
                                            <CardContent style={{ padding: '28px' }}>
                                                <div style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'flex-start',
                                                    marginBottom: '20px',
                                                    gap: '16px'
                                                }}>
                                                    <div style={{ flex: 1 }}>
                                                        <Typography variant="h6" style={{
                                                            fontWeight: '600',
                                                            marginBottom: '12px',
                                                            color: '#333333',
                                                            lineHeight: '1.4'
                                                        }}>
                                                            {index + 1}. {bundle.bundleName.split('.')[0]}
                                                        </Typography>
                                                        <Typography variant="body2" style={{
                                                            color: '#666666',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '8px',
                                                            marginBottom: '20px',
                                                            fontWeight: '500'
                                                        }}>
                                                            <Package size={16} />
                                                            {t('org.bundles.documentCount', { count: bundle.bundleTemplates?.length || 0 })}
                                                        </Typography>

                                                        <div style={{
                                                            backgroundColor: colors.neutral[50],
                                                            borderRadius: '16px',
                                                            padding: '20px',
                                                            marginBottom: '24px',
                                                            border: '1px solid #EAEEF2'
                                                        }}>
                                                            <SimpleSnackbar
                                                                templateLink={`${currentProtocol}//${currentDomain}${port}/bundle/${bundle.bundleID}`}
                                                                name={bundle.bundleName.split('.')[0]}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>

                                                <div style={{
                                                    backgroundColor: '#f0f8ff',
                                                    borderRadius: '16px',
                                                    padding: '24px',
                                                    border: `2px solid ${colors.secondary.main}`
                                                }}>
                                                    <Typography variant="h6" style={{
                                                        fontWeight: '600',
                                                        marginBottom: '16px',
                                                        color: '#333333'
                                                    }}>
                                                        {t('org.bundles.documentsInBundle')}
                                                    </Typography>
                                                    <ol style={{ margin: 0, paddingRight: '24px' }}>
                                                        {bundle.bundleTemplates?.map((template, templateIndex) => (
                                                            <li key={templateIndex} style={{
                                                                marginBottom: '12px',
                                                                padding: '12px 0',
                                                                borderBottom: templateIndex < bundle.bundleTemplates.length - 1 ? `1px solid ${colors.secondary.main}` : 'none'
                                                            }}>
                                                                <Typography variant="body2" style={{
                                                                    fontWeight: '600',
                                                                    color: '#333333'
                                                                }}>
                                                                    {removeAfterLastUnderscore(template.name.split('.')[0])}
                                                                </Typography>
                                                            </li>
                                                        ))}
                                                    </ol>
                                                </div>
                                            </CardContent>

                                            <CardActions style={{
                                                borderTop: '1px solid #EAEEF2',
                                                padding: '24px',
                                                justifyContent: 'flex-end'
                                            }}>
                                                <StyledButton
                                                    variant="contained"
                                                    style={{ backgroundColor: colors.secondary.main }}
                                                >
                                                    <a
                                                        target="_blank"
                                                        href={`${currentProtocol}//${currentDomain}${port}/bundle/${bundle.bundleID}`}
                                                        style={{
                                                            color: 'inherit',
                                                            textDecoration: 'none',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '8px'
                                                        }}
                                                    >
                                                        {t('org.bundles.openInNewTab')} <LaunchIcon fontSize="small" />
                                                    </a>
                                                </StyledButton>
                                            </CardActions>
                                        </BrandCard>
                                    ))}
                                </CardContent>
                            </BrandCard>
                        )}
                    </Box>

                    {/* Bundle Creation Modal */}
                    <Modal
                        open={openBundleCreator}
                        onClose={handleClose}
                        aria-labelledby="bundle-modal-title"
                        aria-describedby="bundle-modal-description"
                    >
                        <Box
                            sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                width: { xs: '90%', sm: 500 },
                                bgcolor: 'background.paper',
                                borderRadius: '20px',
                                boxShadow: 'rgba(26, 0, 0, 0.2) 0 20px 60px',
                                p: 0,
                                border: 'none'
                            }}
                            role="dialog"
                        >
                            <div style={{ padding: '40px' }}>
                                <Typography
                                    id="bundle-modal-title"
                                    variant="h5"
                                    style={{
                                        fontWeight: '600',
                                        marginBottom: '32px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '12px',
                                        color: '#333333',
                                        lineHeight: '1.4'
                                    }}
                                >
                                    <Package size={28} aria-hidden="true" />
                                    {t('org.bundles.modalTitle')}
                                </Typography>

                                <form onSubmit={handleSubmit} aria-labelledby="bundle-modal-title">
                                    <TextField
                                        label={t('org.bundles.bundleNameLabel')}
                                        value={bundleName}
                                        onChange={handleInputChange}
                                        fullWidth
                                        variant="outlined"
                                        required
                                        inputRef={bundleNameInputRef}
                                        inputProps={{
                                            'aria-label': t('org.bundles.bundleNameAriaLabel'),
                                            'aria-required': 'true'
                                        }}
                                        sx={{
                                            mt: 2,
                                            mb: 3,
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '12px',
                                                '&:hover fieldset': {
                                                    borderColor: colors.secondary.main,
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: colors.primary.main,
                                                }
                                            }
                                        }}
                                    />

                                    <Typography
                                        id="bundle-modal-description"
                                        variant="h6"
                                        style={{
                                            marginBottom: '16px',
                                            fontWeight: '600',
                                            color: '#333333'
                                        }}
                                    >
                                        {t('org.bundles.selectDocumentsLabel')}
                                    </Typography>
                                    <div style={{
                                        maxHeight: '300px',
                                        overflowY: 'auto',
                                        backgroundColor: colors.neutral[50],
                                        borderRadius: '16px',
                                        padding: '20px',
                                        border: '2px solid #EAEEF2',
                                        marginBottom: '32px'
                                    }}>
                                        {selectedTemplates.map((template) => (
                                            <FormControlLabel
                                                key={template.index}
                                                control={
                                                    <Checkbox
                                                        checked={template.selected}
                                                        onChange={() => handleOptionChange(template.index)}
                                                        sx={{
                                                            color: colors.secondary.main,
                                                            '&.Mui-checked': {
                                                                color: colors.primary.main,
                                                            }
                                                        }}
                                                    />
                                                }
                                                label={`${template.name} (${template.id})`}
                                                sx={{
                                                    width: '100%',
                                                    mb: 1,
                                                    backgroundColor: template.selected ? 'rgba(19, 36, 161, 0.1)' : 'transparent',
                                                    borderRadius: '12px',
                                                    margin: '8px 0',
                                                    padding: '12px',
                                                    border: template.selected ? `2px solid ${colors.primary.main}` : '2px solid transparent',
                                                    transition: 'all 0.3s ease'
                                                }}
                                            />
                                        ))}
                                    </div>

                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'flex-end',
                                        gap: '16px',
                                        flexWrap: 'wrap'
                                    }}>
                                        <StyledButton
                                            onClick={handleClose}
                                            variant="outlined"
                                            aria-label={t('org.bundles.cancelAriaLabel')}
                                        >
                                            {t('org.bundles.cancelButton')}
                                        </StyledButton>
                                        <StyledButton
                                            type="submit"
                                            variant="contained"
                                            actiontype="primary"
                                            style={{ backgroundColor: colors.secondary.main }}
                                            aria-label={t('org.bundles.createAriaLabel')}
                                        >
                                            {t('org.bundles.createBundleButton')}
                                        </StyledButton>
                                    </div>
                                </form>
                            </div>
                        </Box>
                    </Modal>

                    {/* Rename Template Modal */}
                    <Modal
                        open={renameModalOpen}
                        onClose={closeRenameModal}
                        aria-labelledby="rename-modal-title"
                        aria-describedby="rename-modal-description"
                    >
                        <Box
                            sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                width: { xs: '90%', sm: 450 },
                                bgcolor: 'background.paper',
                                borderRadius: radius.xl,
                                boxShadow: shadows.modal,
                                p: 0,
                                border: 'none',
                            }}
                            role="dialog"
                        >
                            <div style={{ padding: '40px' }}>
                                <Typography
                                    id="rename-modal-title"
                                    variant="h5"
                                    style={{
                                        fontWeight: typography.fontWeight.semibold,
                                        marginBottom: spacing.sm,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: spacing.md,
                                        color: colors.text.primary,
                                        lineHeight: '1.4',
                                    }}
                                >
                                    <DriveFileRenameOutlineIcon aria-hidden="true" />
                                    {t('org.documents.renameTitle')}
                                </Typography>
                                <Typography
                                    id="rename-modal-description"
                                    variant="body2"
                                    style={{
                                        color: colors.text.muted,
                                        marginBottom: spacing.lg,
                                    }}
                                >
                                    {t('org.documents.renameDescription')}
                                </Typography>

                                <TextField
                                    label={t('org.documents.renameLabel')}
                                    value={newTemplateName}
                                    onChange={(e) => setNewTemplateName(e.target.value)}
                                    fullWidth
                                    variant="outlined"
                                    inputRef={renameInputRef}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && newTemplateName.trim() && newTemplateName.trim() !== renameTarget?.currentName) {
                                            handleRename();
                                        }
                                    }}
                                    sx={{
                                        mb: 3,
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: radius.card,
                                            '&:hover fieldset': {
                                                borderColor: colors.secondary.main,
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: colors.primary.main,
                                            },
                                        },
                                    }}
                                />

                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    gap: spacing.md,
                                    flexWrap: 'wrap',
                                }}>
                                    <StyledButton
                                        onClick={closeRenameModal}
                                        variant="outlined"
                                        aria-label={t('org.documents.renameCancelAriaLabel')}
                                    >
                                        {t('common.cancel')}
                                    </StyledButton>
                                    <StyledButton
                                        onClick={handleRename}
                                        variant="contained"
                                        actiontype="primary"
                                        disabled={!newTemplateName.trim() || newTemplateName.trim() === renameTarget?.currentName || renaming}
                                        aria-label={t('org.documents.renameSaveAriaLabel')}
                                    >
                                        {renaming ? t('common.loading') : t('common.save')}
                                    </StyledButton>
                                </div>
                            </div>
                        </Box>
                    </Modal>
                </ContentWrapper>
            )}
        </PageContainer>
    );
}

const data = {
    day: "",
    month: "",
    year: "",
    childName: "",
    childId: "",
    parentName1: "",
    parentId1: "",
    phoneNumber1: "",
    parentName2: "",
    parentId2: "",
    phoneNumber2: "",
    kindergarten: "",
    hebrewYear: "",
    childFirstName: "",
    childLastName: "",
    dateOfBirth: "",
    countryOfBirth: "",
    yearOfArrival: "",
    address: "",
    zip: "",
    brother1: "",
    brother2: "",
    brother3: "",
    brother4: "",
    parentJob1: "",
    parentHomeNumber1: "",
    parentEmailAddress1: "",
    parentJob2: "",
    parentHomeNumber2: "",
    parentEmailAddress2: "",
    relativeName1: "",
    relativeStatus1: "",
    relativeNumber1: "",
    relativeName2: "",
    relativeStatus2: "",
    relativeNumber2: "",
    healthIssueExist: "false",
    healthIssueAndSolution: "",
    allergyToMedication: "",
    allergyToFood: "",
    pastDiseases: "",
    allergies: "",
    receivedFullVaccination: "true",
    nonReceivedVaccinations: "",
    hmo: "",
    remarks: "",
    attendanceStartingDate: "",
    from: "",
    signingDate: "",
    className: "",
    monthlyPayment: "",
    paymentMethod: "12-checks",
    allowsPhotographingInternal: "false",
    allowsPhotographingExternal: "false",
    approverName: "",
    approverStatus: "",
    approverAddress: "",
    approverPhoneNumber: "",

    doctorName: "",
    previousKindergarten: "",
    parentWorkPlace1: "",
    parentWorkPlace2: "",
    parentWorkPhoneNumber1: "",
    parentWorkPhoneNumber2: "",
    childOrderBetweenSiblings: "",
    emergencyContactName: "",
    emergencyContactNumber: "",

    signature1: "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAVQAAADICAYAAAC3QRk5AAAAAXNSR0IArs4c6QAABmJJREFUeF7t1DENADAMBLEEQPnTrVQKvdEB8IMV3c7MGUeAAAEC3wIrqN+GBggQIPAEBNUjECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIELjoXCvGAGlIAAAAAAElFTkSuQmCC",
    signature2: "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAVQAAADICAYAAAC3QRk5AAAAAXNSR0IArs4c6QAABmJJREFUeF7t1DENADAMBLEEQPnTrVQKvdEB8IMV3c7MGUeAAAEC3wIrqN+GBggQIPAEBNUjECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIEBNUPECBAIBIQ1AjSDAECBATVDxAgQCASENQI0gwBAgQE1Q8QIEAgEhDUCNIMAQIELjoXCvGAGlIAAAAAAElFTkSuQmCC",
    pdfFileURL: "",
};